import {
  type GetWebflowSiteConfigByWebflowSiteIdDto,
  type SiteAddonType,
  type WebflowSiteConfigDto,
} from "@relkit/shared";
import { compact } from "lodash-es";
import { AddonRegistry } from "./addon-registry";
import { ConditionMap } from "./condition-map";
import { getConsumerId } from "./consumer-id";
import { $, $rk } from "./dom";
import { createFetcher } from "./fetcher";

export * from "./adder";
export * from "./addon-registry";
export * from "./condition-map";
export * from "./core";
export * from "./debounce";
export * from "./dom";
export * from "./event-notifier";
export * from "./input";
export * from "./maths";
export * from "./nubs";
export * from "./pagination";
export * from "./sattr";
export * from "./url";
export * from "./webflow";

declare global {
  var __API_URL__: string;
  var __CDN_URL__: string;
  /** SRI hashes of the addon modules, baked in at build time by addon type. */
  var __ADDON_MODULE_INTEGRITY__: Record<string, string | undefined>;

  interface Window {
    Relkit: unknown;
  }
}

export const addonRegistry = new AddonRegistry();

const callbacks: VoidFunction[] = [];

if (Array.isArray(window.Relkit)) {
  const queuedEntries = window.Relkit as unknown[];

  for (let index = 0; index < queuedEntries.length; ++index) {
    const queuedEntry = queuedEntries[index];

    if (typeof queuedEntry === "string") {
      const addonName = queuedEntry;
      const addonCallback = queuedEntries[index + 1] as VoidFunction;

      addonRegistry.onReady(addonName, addonCallback);

      ++index;

      continue;
    }

    callbacks.push(queuedEntry as VoidFunction);
  }
}

function executePushCallback(callback: VoidFunction) {
  try {
    callback();
  } catch (error) {
    console.error(error);
  }
}

let isDomReady = false;

export function push(callback: () => void): void;
export function push<T>(addonName: string, callback: (api: T) => void): void;
export function push<T>(
  callbackOrAddonName: (() => void) | string,
  addonCallback?: (api: T) => void,
) {
  if (typeof callbackOrAddonName === "string") {
    addonRegistry.onReady(callbackOrAddonName, addonCallback!);

    return;
  }

  if (isDomReady) {
    executePushCallback(callbackOrAddonName);
  } else {
    callbacks.push(callbackOrAddonName);
  }
}

export let room: string | null = null;

export const params = (() => {
  const params = Array.from(
    new URLSearchParams(window.location.search).entries(),
  );

  return params.reduce(
    (acc, [key, value]) => {
      acc[key] = value;
      return acc;
    },
    {} as Record<string, string>,
  );
})();

/**
 * Reads one of the script tag's own attributes.
 *
 * Names are `data-` prefixed because Webflow's Custom Code API rejects any
 * attribute that is not. Builds from before the App installed itself read the
 * bare `rk-*` names instead; the two are not interchangeable, and a site is
 * always served whichever build its own script tag points at.
 */
function readScriptAttribute(name: string): string {
  return $.make(`script[${name}]`).attr(name) ?? "";
}

export const webflowSiteId = readScriptAttribute("data-rk-site-id");
export const scriptVersion = readScriptAttribute("data-rk-script-version");
export const scriptUniqueId = readScriptAttribute("data-rk-script-unique-id");

export const isDev =
  new URLSearchParams(window.location.search).get("dev") === "1";

const pageLang = document.documentElement.lang;

export const fetch = createFetcher(`${__API_URL__}/api/v1`, {
  headers: {
    "x-webflow-site-id": webflowSiteId,
    "x-consumer-id": getConsumerId(),
    ...(pageLang ? { "x-lang": pageLang } : {}),
  },
});

export let siteConfig: WebflowSiteConfigDto | null = null;

export const globalConditionMap: ConditionMap = new ConditionMap();

export async function bootstrap() {
  const isStaging =
    location.hostname.endsWith(".webflow.io") ||
    location.hostname.endsWith(".canvas.webflow.com");
  const isProd = !isStaging;

  const { getItem, setItem } = (() => {
    const key = (suffix: string) => `rk:${window.location.hostname}:${suffix}`;

    return {
      getItem: (suffix: string) => window.sessionStorage.getItem(key(suffix)),
      setItem: (suffix: string, value: string) =>
        window.sessionStorage.setItem(key(suffix), value),
    };
  })();

  const now = Date.now();
  const currentOriginPath = location.origin + location.pathname;

  let cachedJson = null;
  let cachedOrigin = null;
  let cachedTs = 0;

  try {
    cachedJson = getItem("site-config");
    cachedOrigin = getItem("site-config-origin");
    cachedTs = Number.parseInt(getItem("site-config-timestamp") || "0");
  } catch {}

  const hasCache = !!cachedJson;
  const age = now - cachedTs;
  const isTtlValid = age >= 0 && age < 1000 * 60 * 5;

  const isPageReload = cachedOrigin && cachedOrigin === currentOriginPath;

  const addonTypes: SiteAddonType[] = compact([
    !$rk("filter-list").filter$(($element) => !!$element.rk("filter-list"))
      .empty && "filter",
    !$rk("item-interactions-slug").filter$(
      ($element) => !!$element.rk("item-interactions-slug"),
    ).empty && "item_interactions",
  ]);

  if (addonTypes.length === 0) {
    return;
  }

  if (isProd && !isPageReload && hasCache && isTtlValid) {
    try {
      siteConfig = JSON.parse(cachedJson!);
    } catch {}
  }

  if (!siteConfig) {
    siteConfig = await fetch<GetWebflowSiteConfigByWebflowSiteIdDto>({
      method: "GET",
      pathname: "/config",
    }).then((result) => result.siteConfig);

    setItem("site-config", JSON.stringify(siteConfig));
    setItem("site-config-origin", currentOriginPath);
    setItem("site-config-timestamp", now.toString());
  }

  if (!siteConfig) {
    throw new Error("No config found");
  }

  const scriptUrlByAddonType: Record<SiteAddonType, string> = {
    filter: `${__CDN_URL__}/${scriptVersion}/filter.js`,
    item_interactions: `${__CDN_URL__}/${scriptVersion}/item-interactions.js`,
    test: `${__CDN_URL__}/${scriptVersion}/test.js`,
  };

  for (const addonType of addonTypes) {
    const scriptUrl = new URL(scriptUrlByAddonType[addonType]);

    if (scriptUniqueId) {
      scriptUrl.searchParams.set("f", scriptUniqueId);
    }

    if (scriptUrl) {
      const script = document.createElement("script");
      script.src = scriptUrl.toString();

      // Modules come from the same immutable directory this build was served
      // from, pinned by a hash baked in at build time. The browser then refuses
      // any module whose bytes differ from the ones reviewed alongside it.
      const integrity = __ADDON_MODULE_INTEGRITY__[addonType];

      if (integrity) {
        script.integrity = integrity;
        // Required for the browser to check integrity on a cross-origin file.
        script.crossOrigin = "anonymous";
      }

      document.head.appendChild(script);
    }
  }
}

/**
 * Guards against Relkit running twice on the same page.
 *
 * A site can carry both the snippet its owner pasted by hand and the script
 * the App now applies through Webflow's Custom Code API — during the migration
 * from one to the other, both are present. Booting twice would double every
 * request and wire each addon's event handlers twice over.
 *
 * The first build to reach this point wins; later ones stop here.
 */
const BOOTSTRAP_FLAG = "__rk_bootstrapped";

function hasAlreadyBootstrapped(): boolean {
  const globalScope = window as unknown as Record<string, unknown>;

  if (globalScope[BOOTSTRAP_FLAG]) {
    return true;
  }

  globalScope[BOOTSTRAP_FLAG] = true;

  return false;
}

function onDomReady() {
  isDomReady = true;

  for (const callback of callbacks) {
    executePushCallback(callback);
  }

  if (webflowSiteId && !hasAlreadyBootstrapped()) {
    bootstrap();
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    onDomReady();
  });
} else {
  queueMicrotask(() => {
    onDomReady();
  });
}
